import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box, HStack, VStack, Text, Badge, Alert, AlertIcon, Button, Collapse, Code,
} from "@chakra-ui/react";
import { readCache, writeCache, formatAge, CODE_REVIEW_KEY } from "../../../utils/resultCache";
import AssignModal from "../../../components/messaging/AssignModal";
import { SourceTaskChip, useSourceTasks } from "./issueTasks";
import { ImpactChip, ImpactDonut, RiskDonut } from "./StatusDonut";
import {
  byImpact, checkImpact, codeCheckEffect, findingEffect, findingImpact, pctOf, splitHealthAndRisk, stateOf, withEffect,
  worseOf,
} from "./impact";
import NightlyReview from "./NightlyReview";
import { aiFindingTaskText, useCodeFindings, useSizing, AiFindingCard, ClosedFindings, SizingNote } from "./AiFindings";
import { useLastNight, pickNewer, LastNightLabel } from "./LastNight";

/**
 * Fourth tab of the Live Activity modal: a general review of the code itself, for what the
 * Memory Issues tab (the process's memory) and the DB Health Check (the live database) don't
 * look at: things that break a deploy, leak a secret, open an endpoint, hang a request or hide
 * an error. The backend runs the checks and writes the wording (api/code_review/checks.py);
 * this panel lays it out worst first, with who to ask (from git blame of the lines found)
 * and an Assign task button (source_kind "code_review", source_ref = the check id).
 *
 * Two parts, each with its button at the top, like the Memory Issues tab: "Review new commits" has
 * Claude review the commits pushed since the last review (the nightly AI review, run now: it takes a
 * few minutes on the server, api/code_review/ai_review.py, and the tab asks how it's going), and
 * "Run checks" runs the whole-codebase checks. The AI review's open "general" findings from every
 * night come first (memory and database ones are filed in their own tabs), each with Assign task
 * and Dismiss. The whole-codebase checks show last night's automatic run when it's newer than the
 * last one run from this browser.
 *
 * How bad each one is: every check carries an impact (the share of users at risk, estimated per kind
 * of check in api/code_review/checks.py) and every AI finding the review's own estimate. The summary
 * is the tab's health from those (impact.js), so a pile of small warnings doesn't read as red.
 */

const STATUS = {
  problem: { label: "PROBLEM", scheme: "red", order: 0 },
  warning: { label: "WARNING", scheme: "orange", order: 1 },
  unknown: { label: "COULDN'T CHECK", scheme: "gray", order: 2 },
  ok: { label: "OK", scheme: "green", order: 3 },
};
const VERDICT = {
  problem: { scheme: "red", label: "FIX NOW" },
  warning: { scheme: "orange", label: "CLEAN UP" },
  ok: { scheme: "green", label: "CLEAN" },
};
// A task gets at most this many places; the rest stay on this tab.
const TASK_PLACES = 15;
// What a check's impact is a share of, when it was saved before checks said.
const CHECK_OF = "users";

/** A check's impact: broken now only for code that can't run at all; every other finding is a risk. */
export function codeImpact(check) {
  return withEffect(checkImpact(check, CHECK_OF), codeCheckEffect(check?.id, check?.status));
}

/** A nightly review finding's impact: a risk, not something failing now. */
function aiImpact(finding) {
  return withEffect(findingImpact(finding), findingEffect(finding));
}
// The summary's colour for each state of the tab's health (impact.js).
const TONE_OF_STATE = { urgent: "red", minor: "yellow", good: "green" };

// The review in progress, per run function, so leaving the tab mid-review doesn't lose it.
const inFlight = new WeakMap();

function startReview(run) {
  let review = inFlight.get(run);
  if (!review) {
    review = Promise.resolve()
      .then(() => run())
      .then((result) => {
        writeCache(CODE_REVIEW_KEY, result);
        return result;
      })
      .finally(() => inFlight.delete(run));
    inFlight.set(run, review);
  }
  return review;
}

function plural(n, one, many = `${one}s`) {
  return `${n} ${n === 1 ? one : many}`;
}

/** What the last "Review new commits" did, in one line. */
export function aiRunText(result) {
  if (!result) return "";
  if (!result.reviewed && !result.deferred) return "no new commits since the last review.";
  const parts = [`reviewed ${plural(result.reviewed, "new commit")}`];
  const areas = result.by_area || {};
  const where = [areas.general && `${areas.general} here`, areas.memory && `${areas.memory} in Memory Issues`,
                 areas.database && `${areas.database} in DB Health Check`].filter(Boolean).join(", ");
  parts.push(result.findings ? `${plural(result.findings, "finding")}${where ? ` (${where})` : ""}` : "no problems found");
  if (result.fixed) parts.push(`${plural(result.fixed, "earlier finding")} fixed`);
  if (result.deferred) parts.push(`${plural(result.deferred, "commit")} left for the next review`);
  return `${parts.join(" · ")}.`;
}

/** The "Review new commits" run on the server: start it, and ask how it's going while it runs. */
function useAiReview(startAi, loadStatus, pollMs, onFinished) {
  const [status, setStatus] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const finished = useRef(onFinished);
  finished.current = onFinished;

  useEffect(() => {
    if (!loadStatus) return undefined;
    let cancelled = false;
    loadStatus().then((s) => { if (!cancelled) setStatus(s); }).catch(() => {});
    return () => { cancelled = true; };
  }, [loadStatus]);

  const running = status?.state === "running";
  useEffect(() => {
    if (!running || !loadStatus) return undefined;
    let cancelled = false;
    let over = false;     // a second look already on its way when the first says "done" changes nothing
    const timer = setInterval(() => {
      loadStatus().then((s) => {
        if (cancelled || over) return;
        setStatus(s);
        if (s.state !== "running") {
          over = true;
          clearInterval(timer);
          finished.current?.();
        }
      }).catch(() => {});
    }, pollMs);
    return () => { cancelled = true; clearInterval(timer); };
  }, [running, loadStatus, pollMs]);

  const start = useCallback(() => {
    setStarting(true);
    setError(null);
    startAi()
      .then((s) => setStatus(s))
      .catch((e) => setError(e?.message || String(e)))
      .finally(() => setStarting(false));
  }, [startAi]);

  return { status, error, starting, start, running };
}

function AiRunStatus({ ai }) {
  const s = ai.status;
  if (ai.error) {
    return (
      <Alert status="error" borderRadius="md" fontSize="sm" data-testid="code-review-ai-error">
        <AlertIcon />Couldn't start the AI review: {ai.error}
      </Alert>
    );
  }
  if (!s) return null;
  if (!s.available) {
    return (
      <Text fontSize="xs" color="gray.600" data-testid="code-review-ai-status">
        The AI review can't run on this server: {s.why}
      </Text>
    );
  }
  if (s.state === "running") {
    const started = `started ${formatAge(s.started_at)}${s.started_by ? ` by ${s.started_by}` : ""}`;
    return (
      <Text fontSize="xs" color="purple.700" data-testid="code-review-ai-status">
        {s.phase === "setting_up"
          ? `Getting the AI review ready on this server first (once after each deploy, about a minute), then Claude `
            + `reviews the commits pushed since the last review (${started}). You can leave this tab.`
          : `Claude is reviewing the commits pushed since the last review (${started}). It takes a few minutes; you `
            + "can leave this tab."}
      </Text>
    );
  }
  if (s.state === "done") {
    return (
      <Text fontSize="xs" color="gray.700" data-testid="code-review-ai-status">
        Last AI review, {formatAge(s.finished_at)}: {aiRunText(s.result)}
      </Text>
    );
  }
  if (s.state === "failed") {
    return (
      <Alert status="warning" borderRadius="md" fontSize="sm" data-testid="code-review-ai-status">
        <AlertIcon />{s.error}
      </Alert>
    );
  }
  return null;
}

/**
 * The whole tab at a glance, like the Memory Issues tab: the AI review's open findings and the
 * whole-codebase checks together, as the tab's health. Red = something needs fixing now (one issue
 * of 25% or more), yellow = minor issues (however many add up), green = nothing that matters.
 */
function OverallSummary({ report, aiFindings }) {
  const counts = report?.counts || {};
  const aiProblems = aiFindings.filter((f) => f.severity === "problem").length;
  const aiWarnings = aiFindings.length - aiProblems;
  const clean = counts.ok || 0;
  const warnings = (counts.warning || 0) + aiWarnings;
  const problems = (counts.problem || 0) + aiProblems;
  const unknown = counts.unknown || 0;
  const items = [
    ...(report?.checks || []).map((c) => ({ key: c.id, label: c.title, impact: codeImpact(c) })),
    ...aiFindings.map((f) => ({ key: f.id, label: f.title, impact: aiImpact(f) })),
  ];
  const state = stateOf(items);
  const rings = splitHealthAndRisk(items);
  const tone = TONE_OF_STATE[state];
  const meta = { red: { scheme: "red", label: "FIX NOW" }, yellow: { scheme: "orange", label: "CLEAN UP" },
                 green: { scheme: "green", label: problems || warnings ? "LOOKS GOOD" : "ALL CLEAR" } }[tone];
  const headline = problems
    ? `${plural(problems, "problem")} to fix${warnings ? ` · ${plural(warnings, "warning")}` : ""}`
    : warnings ? `${plural(warnings, "warning")} to clean up` : "Nothing open";
  const checksText = report
    ? `${plural((report.checks || []).length, "whole-codebase check")}: ${clean} clean, `
      + `${plural(counts.warning || 0, "warning")}, ${plural(counts.problem || 0, "problem")}`
      + (unknown ? `, ${unknown} couldn't run` : "")
    : "whole-codebase checks not run yet";
  return (
    <Box px={4} py={3} borderRadius="md" borderWidth="2px" bg={`${meta.scheme}.50`} borderColor={`${meta.scheme}.400`}
         data-testid="code-review-summary" data-tone={tone} data-state={state} role="status">
      <HStack justify="space-between" align="center" spacing={4} flexWrap="wrap">
        <VStack align="start" spacing={1}>
          <HStack spacing={3} flexWrap="wrap">
            <Badge colorScheme={meta.scheme} fontSize="sm" px={2}>{meta.label}</Badge>
            <Text fontSize="lg" fontWeight="bold">{headline}</Text>
          </HStack>
          <Text fontSize="xs" color="gray.600">
            {plural(aiFindings.length, "open finding")} from the nightly AI review · {checksText}
          </Text>
        </VStack>
        {/* Two rings: what's broken now (health), and what could go wrong (risk). */}
        <HStack spacing={6} align="start" flexWrap="wrap">
          <ImpactDonut testId="code-review-pie" label="Code" items={rings.health} nothingText="Nothing is broken now" />
          <RiskDonut testId="code-review-risk" label="Code risk" items={rings.risk} />
        </HStack>
      </HStack>
    </Box>
  );
}

function byWorstFirst(checks) {
  return [...(checks || [])].sort((a, b) => (STATUS[a.status]?.order ?? 9) - (STATUS[b.status]?.order ?? 9));
}

function placeText(p) {
  return `${p.file}:${p.line}${p.detail ? ` — ${p.detail}` : ""}`;
}

/** Title and description for a task made from one check. */
export function reviewTaskText(check, report) {
  const lines = [`${(STATUS[check.status] || STATUS.unknown).label}: ${check.headline}`];
  if (check.why) lines.push(`Why it matters: ${check.why}`);
  if (check.fix) lines.push(`What to do: ${check.fix}`);
  const places = check.places || [];
  if (places.length) {
    lines.push("Where:", ...places.slice(0, TASK_PLACES).map((p) => `  ${placeText(p)}`));
    if ((check.count || places.length) > TASK_PLACES) {
      lines.push(`  …and ${(check.count || places.length) - TASK_PLACES} more in SuperAdmin → Live Activity → Code Review`);
    }
  }
  if (check.owners?.length) lines.push(`Suggested to fix: ${check.owners.join(", ")}`);
  const when = report?.ran_at ? new Date(report.ran_at).toLocaleString() : "";
  lines.push(`Found by the code review${when ? ` on ${when}` : ""} · check id ${check.id}`);
  return { title: `Code review: ${check.title}`, description: lines.join("\n") };
}

function Owners({ check, ownersFrom }) {
  if (check.status === "ok" || !check.owners?.length) return null;
  return (
    <Text fontSize="xs" color="red.700" data-testid={`code-review-owners-${check.id}`}
          title={`Ranked by ${ownersFrom || "who wrote the lines found"}`}>
      Suggested to fix: {check.owners.join(", ")}
    </Text>
  );
}

function Places({ check }) {
  const places = check.places || [];
  const more = (check.count || places.length) - places.length;
  return (
    <Box mt={2} p={2} bg="gray.50" borderRadius="md" maxH="260px" overflowY="auto"
         data-testid={`code-review-places-${check.id}`}>
      <VStack align="stretch" spacing={0.5}>
        {places.map((p) => (
          <Text key={`${p.file}:${p.line}:${p.detail}`} fontSize="xs">
            <Code fontSize="xs">{p.file}:{p.line}</Code>{p.detail ? ` ${p.detail}` : ""}
          </Text>
        ))}
        {more > 0 && <Text fontSize="xs" color="gray.600">…and {more} more.</Text>}
      </VStack>
    </Box>
  );
}

function CheckCard({ check, ownersFrom, task, canAssign, onAssign }) {
  const meta = STATUS[check.status] || STATUS.unknown;
  const [open, setOpen] = useState(false);
  const found = (check.places || []).length > 0;
  return (
    <Box borderWidth="1px" borderLeftWidth="4px" borderLeftColor={`${meta.scheme}.400`} borderRadius="md" p={3}
         data-testid={`code-review-check-${check.id}`} data-status={check.status}>
      <HStack justify="space-between" align="flex-start" spacing={2} mb={1}>
        <HStack spacing={2} flexWrap="wrap">
          <Badge colorScheme={meta.scheme}>{meta.label}</Badge>
          <Text fontWeight="bold">{check.title}</Text>
          <ImpactChip impact={codeImpact(check)} testId={`code-review-impact-${check.id}`} />
        </HStack>
        <Button size="xs" variant="outline" flexShrink={0} data-testid={`code-review-assign-${check.id}`}
                isDisabled={!canAssign} onClick={() => onAssign(check)}>
          Assign task
        </Button>
      </HStack>
      <Text fontSize="md" fontWeight="semibold" mb={0.5}>{check.headline}</Text>
      <Box mb={2}><Owners check={check} ownersFrom={ownersFrom} /></Box>
      {task && <Box mt={-1} mb={2}><SourceTaskChip task={task} testId={`code-review-task-${check.id}`} /></Box>}
      {check.why && (
        <Box mb={2}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">WHY IT MATTERS</Text>
          <Text fontSize="sm">{check.why}</Text>
        </Box>
      )}
      {check.fix && (
        <Box mb={1}>
          <Text fontSize="xs" fontWeight="bold" color="gray.600">WHAT TO DO</Text>
          <Text fontSize="sm">{check.fix}</Text>
        </Box>
      )}
      {found && (
        <Button size="xs" variant="link" colorScheme="blue" onClick={() => setOpen((o) => !o)}
                data-testid={`code-review-toggle-${check.id}`}>
          {open ? "Hide where" : `Show where (${(check.count || check.places.length).toLocaleString()})`}
        </Button>
      )}
      <Collapse in={open} animateOpacity unmountOnExit><Places check={check} /></Collapse>
    </Box>
  );
}

// A passed check keeps showing a task it already has, so the fix can be confirmed.
function PassedRow({ check, task }) {
  const meta = STATUS[check.status] || STATUS.unknown;
  return (
    <Box py={2} borderBottomWidth="1px" borderColor="gray.100" data-testid={`code-review-check-${check.id}`}
         data-status={check.status}>
      <HStack align="baseline" spacing={2} flexWrap="wrap">
        <Badge colorScheme={meta.scheme} variant="subtle">{meta.label}</Badge>
        <Text fontWeight="semibold" fontSize="sm">{check.title}</Text>
        <Text fontSize="sm" color="gray.700">{check.headline}</Text>
      </HStack>
      {task && <SourceTaskChip task={task} testId={`code-review-task-${check.id}`} />}
    </Box>
  );
}

export default function CodeReviewPanel({ run, loadNightly, loadFindings, dismissFinding, loadLastNight, startAi,
                                          loadAiStatus, aiPollMs = 5000, startSizing, loadSizing,
                                          sizingPollMs }) {
  const [entry, setEntry] = useState(() => readCache(CODE_REVIEW_KEY));
  const lastNight = useLastNight("code_review", loadLastNight);
  const found = useCodeFindings("general", loadFindings, dismissFinding);
  // A manual run also sizes the findings saved without an impact (one sizing covers every tab).
  const { sizing, sizeNow } = useSizing(found, startSizing, loadSizing, sizingPollMs);
  const [nightlyKey, setNightlyKey] = useState(0);
  const { reload: reloadFindings } = found;
  // When a review started here finishes: the night by night history and the open findings again.
  const ai = useAiReview(startAi, loadAiStatus, aiPollMs, useCallback(() => {
    setNightlyKey((k) => k + 1);
    reloadFindings();
  }, [reloadFindings]));
  const [running, setRunning] = useState(() => inFlight.has(run));
  const [error, setError] = useState(null);
  const [assigning, setAssigning] = useState(null);
  const [assigningAi, setAssigningAi] = useState(null);   // { finding, run } from the nightly review
  const { me, taskByRef, reloadTasks } = useSourceTasks("code_review", "Code review");
  const mounted = useRef(true);
  const shown = pickNewer(entry, lastNight.run);
  const report = shown?.data || null;

  const follow = useCallback((review) => {
    setRunning(true);
    setError(null);
    review
      .then((result) => { if (mounted.current) setEntry({ data: result, savedAt: new Date().toISOString() }); })
      .catch((e) => { if (mounted.current) setError(e?.message || String(e)); })
      .finally(() => { if (mounted.current) setRunning(false); });
  }, []);

  useEffect(() => {
    mounted.current = true;
    const review = inFlight.get(run);
    if (review) follow(review);
    return () => { mounted.current = false; };
  }, [run, follow]);

  const checks = byWorstFirst(report?.checks);
  // One list of what to fix, most important first: the whole-codebase checks that found something and
  // the nightly AI review's open findings, by how much each puts at risk (the worse status first when
  // two are the same size; findings not sized yet after the ones that are).
  const open = byImpact([
    ...checks.filter((c) => c.status !== "ok")
      .map((c) => ({ kind: "check", id: c.id, item: c, impact: codeImpact(c), worse: worseOf(c.status) })),
    ...(loadFindings && !found.missing ? found.findings : [])
      .map((f) => ({ kind: "ai", id: f.id, item: f, impact: aiImpact(f), worse: worseOf(f.severity) })),
  ], (r) => pctOf(r.impact), (r) => r.worse);
  const passed = checks.filter((c) => c.status === "ok");
  const verdict = VERDICT[report?.overall] || VERDICT.ok;

  return (
    <VStack align="stretch" spacing={4}>
      <Alert status="info" borderRadius="md">
        <AlertIcon />
        <Box>
          <Text fontWeight="bold">Review the code for problems the other tabs don't look for</Text>
          <Text fontSize="sm">
            Two parts. Review new commits has Claude read the commits pushed since the last review for bugs, security
            and privacy holes, and memory and database problems; it also runs by itself every night. Run checks looks
            through the whole codebase for merge conflicts, files that don't compile, secrets in the code, endpoints
            anyone can call, calls that can hang, errors thrown away, SQL pasted together, misspelt settings and more.
            Both only read the code.
          </Text>
        </Box>
      </Alert>

      <HStack justify="flex-end" spacing={2} flexWrap="wrap">
        {startAi && (
          <Button size="sm" variant="outline" colorScheme="blue" data-testid="code-review-ai-run" onClick={ai.start}
                  isLoading={ai.starting || ai.running} loadingText="Claude is reviewing…"
                  isDisabled={ai.status ? !ai.status.available : false}
                  title={ai.status && !ai.status.available ? ai.status.why
                    : `Claude reviews the commits pushed since the last review (a few minutes${ai.status?.setup_needed
                      ? ", plus about a minute the first time after a deploy while the server gets it ready" : ""})`}>
            Review new commits
          </Button>
        )}
        <Button colorScheme="blue" size="sm" onClick={() => { follow(startReview(run)); sizeNow(); }} isLoading={running}
                loadingText="Checking…" data-testid="code-review-run">
          {report ? "Run checks again" : "Run checks"}
        </Button>
      </HStack>
      {startAi && <AiRunStatus ai={ai} />}
      {(report || found.findings.length > 0) && <OverallSummary report={report} aiFindings={found.findings} />}

      {open.length > 0 && (
        <VStack align="stretch" spacing={3} data-testid="code-review-open">
          <Text fontWeight="bold">What to fix, most important first ({open.length})</Text>
          <SizingNote sizing={sizing} testId="code-review-sizing" />
          {open.map((r) => (r.kind === "check"
            ? <CheckCard key={r.id} check={r.item} ownersFrom={report?.owners_from} task={taskByRef[r.id]}
                         canAssign={Boolean(me)} onAssign={setAssigning} />
            : <AiFindingCard key={r.id} finding={r.item} impact={r.impact} task={taskByRef[r.id]}
                             canAssign={Boolean(me)} onAssign={(finding) => setAssigningAi({ finding, night: null })}
                             onDismiss={found.askDismiss} TaskChip={SourceTaskChip} />))}
        </VStack>
      )}
      {found.dismissDialog}

      {loadNightly && (
        <Box borderWidth="1px" borderRadius="md" p={3}>
          <NightlyReview load={loadNightly} reloadKey={nightlyKey} showFindings={!loadFindings || Boolean(found.missing)}
                         taskByRef={taskByRef}
                         canAssign={Boolean(me)} TaskChip={SourceTaskChip}
                         onAssign={(finding, night) => setAssigningAi({ finding, night })}>
            {loadFindings && !found.missing && (
              <Box data-testid="code-review-ai-notes">
                <Text fontSize="sm" color="gray.600" mb={2}>
                  {found.loading ? "" : found.findings.length
                    ? `Its ${found.findings.length} open finding${found.findings.length === 1 ? " is" : "s are"} in the list `
                      + "above, with the checks', marked NIGHTLY AI REVIEW."
                    : "Nothing open from the nightly review."}
                </Text>
                {found.error && <Text fontSize="sm" color="red.600">Couldn't load them: {found.error}</Text>}
                {!found.loading && !found.error && (
                  <ClosedFindings closed={found.closed} toggleClosed={found.toggleClosed}
                                  testId="code-review-ai-closed" />
                )}
              </Box>
            )}
          </NightlyReview>
        </Box>
      )}

      <Text fontWeight="bold" mt={1}>Whole-codebase checks</Text>
      <HStack spacing={3} flexWrap="wrap">
        {report && shown.source === "nightly" && <LastNightLabel run={shown.run} testId="code-review-last-night" />}
        {report && shown.source === "live" && (
          <Text fontSize="xs" color="gray.600" data-testid="code-review-meta">
            Ran {formatAge(entry?.savedAt)}
            {report.duration_ms != null ? ` · took ${(report.duration_ms / 1000).toFixed(1)} s` : ""}
            {report.owners_from ? ` · who to ask: ${report.owners_from}` : ""}
          </Text>
        )}
      </HStack>

      {error && (
        <Alert status="error" borderRadius="md" data-testid="code-review-error">
          <AlertIcon />Couldn't run the code review: {error}
        </Alert>
      )}
      {lastNight.run?.status === "failed" && shown?.source !== "nightly" && (
        <LastNightLabel run={lastNight.run} testId="code-review-last-night" />
      )}
      {!report && !running && !error && (
        <Text fontSize="sm" color="gray.600">
          No review has been run from this browser yet, and the server hasn't run one at night yet. It takes about 20
          seconds.
        </Text>
      )}

      {report && (
        <>
          <Box px={4} py={3} borderRadius="md" borderWidth="2px" bg={`${verdict.scheme}.50`}
               borderColor={`${verdict.scheme}.400`} data-testid="code-review-verdict" role="status">
            <HStack spacing={3} flexWrap="wrap">
              <Badge colorScheme={verdict.scheme} fontSize="sm" px={2}>{verdict.label}</Badge>
              <Text fontSize="lg" fontWeight="bold">{report.headline}</Text>
            </HStack>
          </Box>
          {passed.length > 0 && (
            <Box data-testid="code-review-passed">
              <Text fontSize="sm" fontWeight="bold" color="gray.600" mb={1}>Clean ({passed.length})</Text>
              {passed.map((c) => <PassedRow key={c.id} check={c} task={taskByRef[c.id]} />)}
            </Box>
          )}
        </>
      )}
      {assigningAi && me && (() => {
        const { title, description } = aiFindingTaskText(assigningAi.finding, assigningAi.night);
        return (
          <AssignModal isOpen onClose={() => setAssigningAi(null)} currentUser={me} taskOnly
                       initialTitle={title} initialDescription={description}
                       taskSource={{ source_kind: "code_review", source_ref: assigningAi.finding.id }}
                       onAssigned={reloadTasks} />
        );
      })()}
      {assigning && me && (() => {
        const { title, description } = reviewTaskText(assigning, report);
        return (
          <AssignModal isOpen onClose={() => setAssigning(null)} currentUser={me} taskOnly
                       initialTitle={title} initialDescription={description}
                       taskSource={{ source_kind: "code_review", source_ref: assigning.id }}
                       onAssigned={reloadTasks} />
        );
      })()}
    </VStack>
  );
}
